<template>
  <TsCarouselV2
    v-if="props.tabs.length > 0"
    :pagination="false"
    :navigation="!isMobile"
    :drag-free="true"
    :append-class="twMerge('px-0 -m-1 ', props.appendClass)"
    viewport-class="pb-2"
    prev-button-class="-mt-1"
    next-button-class="-mt-1"
  >
    <template  v-for="(tab, index) in props.tabs">
      <TsCarouselSlide
          v-if="tab"
          :key="tab.id"
          append-class="flex-[0_0_auto] p-1 lg:p-1 "
      >
        <TsButton
            :label="tab.label ?? tab"
            :link="tab.link && typeof tab.link === 'string' ? tab.link : ''"
            :append-class="
         twMerge(
          'px-5 focus:ring-0 text-nowrap border-[#E9E9E9] max-sm:shadow-[0px_4px_12px_0px_#E3E3E399] cursor-pointer',
          active_tag !== tab &&
            'bg-idle-white text-natural-grey hover:text-white font-semibold',
          props.noShadow && 'max-sm:shadow-none shadow-none'
        )
        "
            rounded
            :data-testid="`${props.dataTestid}-${tab.id}-tab`"
            @click="setActiveTag(tab, index)"
            size="sm"
        />
      </TsCarouselSlide>
    </template>

  </TsCarouselV2>
</template>

<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import {
  ref,
  onMounted,
  watch,
} from "vue";

const TsCarouselV2 = defineAsyncComponent(
    () => import('~/components/organisms/TsCarouselV2.vue')
);

const TsCarouselSlide = defineAsyncComponent(
    () => import('~/components/organisms/TsCarouselSlide.vue')
);


type Tag = {
  label: string;
  link?: string;
};

const savelistStore = useSavelistStore();

type Props = {
  tabs?: (Tag | string)[];
  align?: "left" | "center" | "right";
  dataTestid?: string;
  noShadow?: boolean;
  appendClass?: string;
  setActiveTab?: boolean;
};

const {isMobile} = useDevice()

const emit = defineEmits<{
  (event: "tabClick", tab: Tag | string, index: number): void;
}>();

const props = withDefaults(defineProps<Props>(), {
  tabs: () => [
    {
      label: "All",
      link: "",
    },
  ],
  align: "left",
  setActiveTab: true,
});

const active_tag = ref<Tag | string>(
  props.tabs.length > 0 ? props.tabs[0] : ""
);

const setActiveTag = (tab: Tag | string, index: number) => {
  if (active_tag.value === tab) return;
  active_tag.value = tab;
  emit("tabClick", tab, index); // Emit the event correctly
};

// On mount, set the active tab based on the store's index
onMounted(() => {
  const savelistStore = useSavelistStore();


  if (props.setActiveTab) {
    const currentActiveIndex = savelistStore.current_active_savelist.index;

    if (currentActiveIndex === undefined) {
      savelistStore.current_active_savelist.index = 0; // Fallback if index is undefined
    }

    if (currentActiveIndex >= 0 && currentActiveIndex < props.tabs.length) {
      const activeTab = props.tabs[currentActiveIndex];
      setActiveTag(activeTab, currentActiveIndex); // Set the active tab and emit the event
    }
  }
});

// Watch for changes in `current_active_savelist.index` and update the active tab accordingly
watch(
  () => savelistStore.current_active_savelist.index,
  (newIndex) => {
    if (newIndex >= 0 && newIndex < props.tabs.length) {
      const newActiveTab = props.tabs[newIndex];
      setActiveTag(newActiveTab, newIndex); // Update the active tab when index changes
    }
  }
);
</script>
